import { ref, computed, type Ref } from 'vue'
import { sdk } from '@/graphql/client'
import { computeStatus } from '@/lib/eventStatus'

export interface Event {
    id: number
    name: string
    startAt: string | null
    endAt: string | null
    bzucoId: number
}

export type EventStatusFilter = 'all' | 'upcoming' | 'ongoing' | 'past'

export interface EventFilters {
    search: string
    status: EventStatusFilter
    from: string | null
    to: string | null
}

// NFD-strip diacritics for accent-insensitive search.
function normalize(s: string): string {
    return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

function parseDate(s: string | null): number | null {
    if (!s) return null
    const t = new Date(s).getTime()
    return Number.isNaN(t) ? null : t
}

function dayStart(iso: string): number {
    return new Date(`${iso}T00:00:00`).getTime()
}
function dayEnd(iso: string): number {
    return new Date(`${iso}T23:59:59.999`).getTime()
}

export function useEventsData(nowFn: () => number = Date.now) {
    const events: Ref<Event[]> = ref([])
    const loading = ref(false)
    const error = ref<string | null>(null)

    const filters: Ref<EventFilters> = ref({
        search: '',
        status: 'all',
        from: null,
        to: null
    })

    async function refetch() {
        loading.value = true
        error.value = null
        try {
            const r = await sdk.AdminEventsList()
            events.value = (r.admin?.event?.list ?? []) as Event[]
        } catch (e) {
            error.value = (e as Error).message
            events.value = []
        } finally {
            loading.value = false
        }
    }

    const filteredEvents = computed<Event[]>(() => {
        const f = filters.value
        const q = f.search ? normalize(f.search) : ''
        const fromTs = f.from ? dayStart(f.from) : null
        const toTs = f.to ? dayEnd(f.to) : null
        const rangeActive = fromTs !== null || toTs !== null
        const now = nowFn()

        const filtered = events.value.filter(e => {
            if (q && !normalize(e.name).includes(q)) return false

            if (f.status !== 'all') {
                if (computeStatus(e.startAt, e.endAt, now) !== f.status) return false
            }

            if (rangeActive) {
                const s = parseDate(e.startAt)
                if (s === null) return false
                if (fromTs !== null && s < fromTs) return false
                if (toTs !== null && s > toTs) return false
            }
            return true
        })

        return filtered.slice().sort((a, b) => {
            const at = parseDate(a.startAt)
            const bt = parseDate(b.startAt)
            if (at === null && bt === null) return 0
            if (at === null) return 1
            if (bt === null) return -1
            return bt - at
        })
    })

    return { events, filters, filteredEvents, loading, error, refetch }
}
